import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import Spinner from './Spinner';

interface ButtonProps {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md';
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
}

export default function Button({ children, variant = 'primary', size = 'md', fullWidth = false, disabled = false, loading = false, onClick, type = 'button' }: ButtonProps) {
  const base = 'inline-flex items-center justify-center gap-2 rounded-xl font-semibold font-sans leading-6 transition-colors cursor-pointer';
  const width = fullWidth ? 'w-full' : '';
  const disabledClass = disabled || loading ? 'opacity-50 cursor-not-allowed' : '';

  const variants: Record<string, string> = {
    primary: 'bg-gradient-to-br from-primary to-primary-dark text-white shadow-md',
    secondary: 'border-2 border-primary text-text-primary bg-transparent',
    ghost: 'text-text-secondary bg-transparent',
  };

  const sizes: Record<'sm' | 'md', Record<string, string>> = {
    md: {
      primary: 'text-base py-3.5 px-6',
      secondary: 'text-base py-3 px-6',
      ghost: 'text-base py-2 px-4',
    },
    sm: {
      primary: 'text-sm py-2 px-4',
      secondary: 'text-sm py-2 px-4',
      ghost: 'text-sm py-1.5 px-3',
    },
  };

  return (
    <motion.button
      type={type}
      whileTap={!disabled && !loading ? { scale: 0.97 } : undefined}
      className={`${base} ${variants[variant]} ${sizes[size][variant]} ${width} ${disabledClass}`}
      onClick={onClick}
      disabled={disabled || loading}
    >
      {loading ? <Spinner /> : children}
    </motion.button>
  );
}
